import React, {Fragment, useContext, useState} from "react";
import { Dialog, Transition } from "@headlessui/react";
import axios from "axios";
import {OrderPdf} from "../../orders/OrderPdf";
import {pdf} from "@react-pdf/renderer";
import {ViewContext} from "../../../components/view/view";

export function SendOrderSummaryModal({open, setOpen, orderId, orderData, orderItemData, dataSpecs, customers, users,
                                          transponderModels, printTypes, dataRecordTypes, uidFormats, dataListSources}) {
    const viewContext = useContext(ViewContext);
    const [email, setEmail] = useState(orderData?.salesorder_deliverdatato || "");
    const [loading, setLoading] = useState(false);

    const handleSend = async () => {
        // ✅ Basic validation
        if (!email) {
            return alert("Please enter email");
        }

        try {
            setLoading(true);

            // ✅ Generate PDF
            let blob;
            try {
                blob = await pdf(
                    <OrderPdf
                        order={orderData}
                        items={orderItemData}
                        dataSpecs={dataSpecs}
                        customers={customers}
                        users={users}
                        transponderModels={transponderModels}
                        printTypes={printTypes}
                        dataRecordTypes={dataRecordTypes}
                        uidFormats={uidFormats}
                        dataListSources={dataListSources}
                    />
                ).toBlob();
            } catch (pdfError) {
                console.error("PDF Generation Error:", pdfError);
                alert("Failed to generate PDF ❌");
                return;
            }

            // ✅ Prepare form data
            const formData = new FormData();
            formData.append("email", email);
            formData.append("orderPdf", blob, "order_summary.pdf");

            // ✅ API call
            const response = await axios.post(
                `/api/orders/${orderId}/send-package-approval`,
                formData
            );

            // ✅ Backend response handling
            if (!response?.data?.success) {
                viewContext.notification.show("Failed to send Email", 'error', true);
                throw new Error(response?.data?.error || "Failed to send email");
            }

            // ✅ Success
            viewContext.notification.show("Email sent successfully.", 'success', true);
            setOpen(false);
            setEmail("");

        } catch (err) {
            console.error("Send Email Error:", err);

            // ✅ Smart error handling
            let message = "Something went wrong ❌";

            if (err.response) {
                // Backend error
                message = err.response.data?.error || "Server error";
            } else if (err.request) {
                // Network error
                message = "Network error. Please check your connection.";
            } else if (err.message) {
                message = err.message;
            }

            alert(message);

        } finally {
            setLoading(false);
        }
    };
    return (
        <Transition.Root show={!!open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={setOpen}>

                {/* Overlay */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                {/* Center */}
                <div className="fixed inset-0 flex items-center justify-center p-4">

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 scale-95 translate-y-4"
                        enterTo="opacity-100 scale-100 translate-y-0"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 scale-100 translate-y-0"
                        leaveTo="opacity-0 scale-95 translate-y-4"
                    >
                        <Dialog.Panel className="w-full max-w-md bg-white rounded-xl shadow-2xl p-6">

                            {/* Title */}
                            <Dialog.Title className="text-lg font-semibold text-gray-900 text-center">
                                Send Order Summary
                            </Dialog.Title>

                            {/* Subtitle */}
                            <p className="text-sm text-gray-500 text-center mt-1 mb-5">
                                Enter customer email to send package approval.
                            </p>

                            {/* Input */}
                            <input
                                type="email"
                                placeholder="customer@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                            />

                            {/* Buttons */}
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => setOpen(false)}
                                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
                                >
                                    Cancel
                                </button>

                                <button
                                    onClick={handleSend}
                                    disabled={loading}
                                    className="px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow"
                                >
                                    {loading ? "Sending..." : "Send"}
                                </button>
                            </div>

                        </Dialog.Panel>
                    </Transition.Child>

                </div>
            </Dialog>
        </Transition.Root>
    );
}
